import React, { useEffect } from 'react';
import type { StyleProp, ViewStyle } from 'react-native';
import Animated, { useAnimatedStyle, useReducedMotion, useSharedValue, withSequence, withSpring, withTiming } from 'react-native-reanimated';

export interface ApparitionProps {
  /** `pop` : la coche qui grandit ; `fondu` : l'étiquette qui apparaît. */
  effet: 'pop' | 'fondu';
  delai?: number;
  style?: StyleProp<ViewStyle>;
  testID?: string;
  children: React.ReactNode;
}

/**
 * Entrée animée pilotée à la main plutôt que par les animations d'entrée de reanimated (ZoomIn,
 * FadeIn) : mesuré sur l'export web du 03/10/2026, celles-ci restaient figées à leur première image
 * — coches minuscules, étiquettes invisibles. Ici, l'état final est garanti partout ; avec
 * « réduire les animations », il est affiché d'emblée.
 */
export function Apparition({ effet, delai = 0, style, testID, children }: ApparitionProps) {
  const reduit = useReducedMotion();
  const v = useSharedValue(reduit ? 1 : 0);
  useEffect(() => {
    if (reduit) {
      v.value = 1;
      return;
    }
    const entree = effet === 'pop' ? withSpring(1, { damping: 12 }) : withTiming(1, { duration: 250 });
    // Le délai est une attente À VALEUR CONSTANTE plutôt que withDelay, resté sans effet sur le web.
    v.value = delai > 0 ? withSequence(withTiming(0, { duration: delai }), entree) : entree;
  }, [reduit, delai, effet, v]);
  const anime = useAnimatedStyle(() =>
    effet === 'pop' ? { opacity: Math.min(1, v.value * 2), transform: [{ scale: 0.5 + 0.5 * v.value }] } : { opacity: v.value },
  );
  return (
    <Animated.View style={[style, anime]} testID={testID}>
      {children}
    </Animated.View>
  );
}
